import Ajv2020, { type ErrorObject, type ValidateFunction } from "ajv/dist/2020";
import addFormats from "ajv-formats";
import type { FieldErrors, FieldValues, Resolver } from "react-hook-form";
import { evaluate, referencedVars, visibility } from "./conditions";
import { countFields, flattenFields, getAtPath, labelOf, localize, schemaAtPath } from "./schema";
import type { FormIssue, FormLocale, FormRule, FormSchema, PropertySchema, UiSchema } from "./types";
import { getWidget, resolveWidgetKey, widgetOptions } from "./widgets/registry";

const MESSAGES: Record<string, { en: (p: Record<string, unknown>, label: string) => string; ar: (p: Record<string, unknown>, label: string) => string }> = {
  required: { en: (_, l) => `${l} is required.`, ar: (_, l) => `${l} مطلوب.` },
  minLength: { en: (p, l) => `${l} must be at least ${p.limit} characters.`, ar: (p, l) => `${l} يجب أن يحتوي على ${p.limit} أحرف على الأقل.` },
  maxLength: { en: (p, l) => `${l} must be at most ${p.limit} characters.`, ar: (p, l) => `${l} يجب ألا يتجاوز ${p.limit} حرفا.` },
  minimum: { en: (p, l) => `${l} must be ${p.limit} or more.`, ar: (p, l) => `${l} يجب أن يكون ${p.limit} أو أكثر.` },
  exclusiveMinimum: { en: (p, l) => `${l} must be greater than ${p.limit}.`, ar: (p, l) => `${l} يجب أن يكون أكبر من ${p.limit}.` },
  maximum: { en: (p, l) => `${l} must be ${p.limit} or less.`, ar: (p, l) => `${l} يجب أن يكون ${p.limit} أو أقل.` },
  exclusiveMaximum: { en: (p, l) => `${l} must be less than ${p.limit}.`, ar: (p, l) => `${l} يجب أن يكون أقل من ${p.limit}.` },
  multipleOf: { en: (p, l) => `${l} must be a multiple of ${p.multipleOf}.`, ar: (p, l) => `${l} يجب أن يكون من مضاعفات ${p.multipleOf}.` },
  pattern: { en: (_, l) => `${l} has an invalid format.`, ar: (_, l) => `تنسيق ${l} غير صحيح.` },
  format: { en: (p, l) => `${l} must be a valid ${p.format}.`, ar: (p, l) => `${l} يجب أن يكون ${formatAr(String(p.format))} صحيحا.` },
  enum: { en: (_, l) => `${l} must be one of the allowed values.`, ar: (_, l) => `${l} يجب أن يكون من القيم المسموح بها.` },
  const: { en: (_, l) => `${l} has an unexpected value.`, ar: (_, l) => `قيمة ${l} غير متوقعة.` },
  type: { en: (p, l) => `${l} must be a ${p.type}.`, ar: (p, l) => `${l} يجب أن يكون من نوع ${p.type}.` },
  minItems: { en: (p, l) => `${l} needs at least ${p.limit} item(s).`, ar: (p, l) => `${l} يحتاج إلى ${p.limit} عنصر على الأقل.` },
  maxItems: { en: (p, l) => `${l} allows at most ${p.limit} item(s).`, ar: (p, l) => `${l} يسمح بـ ${p.limit} عنصر على الأكثر.` },
  uniqueItems: { en: (_, l) => `${l} contains duplicates.`, ar: (_, l) => `${l} يحتوي على قيم مكررة.` },
  additionalProperties: { en: (_, l) => `${l} contains an unknown field.`, ar: (_, l) => `${l} يحتوي على حقل غير معروف.` },
  oneOf: { en: (_, l) => `${l} does not match exactly one allowed shape.`, ar: (_, l) => `${l} لا يطابق أحد الأشكال المسموح بها.` },
  anyOf: { en: (_, l) => `${l} does not match any allowed shape.`, ar: (_, l) => `${l} لا يطابق أي شكل مسموح به.` },
};

function formatAr(format: string): string {
  return { email: "بريدا إلكترونيا", date: "تاريخا", "date-time": "تاريخا ووقتا", uri: "رابطا", phone: "رقم هاتف" }[format] ?? format;
}

const cache = new WeakMap<FormSchema, ValidateFunction>();

/** Synthetic path holding the review-step declaration checkbox. Stripped from the submitted values. */
export const DECLARATION_PATH = "__declaration";
const DECLARATION_MESSAGE = { en: "Please confirm the declaration before submitting.", ar: "يرجى تأكيد الإقرار قبل الإرسال." };

/** Creates (and caches per schema object) an Ajv validator that tolerates `x-ncp` and unknown formats. */
export function compileSchema(schema: FormSchema): ValidateFunction {
  const cached = cache.get(schema);
  if (cached) return cached;
  const ajv = new Ajv2020({ allErrors: true, strict: false, coerceTypes: false, useDefaults: false, allowUnionTypes: true, validateFormats: true });
  addFormats(ajv);
  ajv.addKeyword({ keyword: "x-ncp" });
  ajv.addFormat("phone", /^\+?[0-9 ()-]{6,20}$/);
  ajv.addFormat("emirates-id", /^784-?\d{4}-?\d{7}-?\d$/);
  ajv.addFormat("trade-licence", /^[A-Za-z0-9/-]{3,32}$/);
  // Presentation-only formats used by widgets; numbers are validated by type/min/max.
  ajv.addFormat("currency", { type: "number", validate: () => true });
  ajv.addFormat("percent", { type: "number", validate: (n: number) => n >= 0 && n <= 100 });
  const validate = ajv.compile(stripEmpty(schema));
  cache.set(schema, validate);
  return validate;
}

/** Ajv rejects `required: []` in some strict modes and treats `type: undefined` badly; normalise before compiling. */
function stripEmpty(schema: PropertySchema): PropertySchema {
  const out: PropertySchema = { ...schema };
  if (out.required && out.required.length === 0) delete out.required;
  if (out.properties) out.properties = Object.fromEntries(Object.entries(out.properties).map(([k, v]) => [k, stripEmpty(v)]));
  if (out.items) out.items = stripEmpty(out.items);
  return out;
}

/** Converts an Ajv instance path (`/parties/0/name`) into dot notation (`parties.0.name`). */
function toDotPath(err: ErrorObject): string {
  const base = err.instancePath.split("/").filter(Boolean).map(decodePointer).join(".");
  if (err.keyword === "required") {
    const missing = String((err.params as { missingProperty?: string }).missingProperty ?? "");
    return base ? `${base}.${missing}` : missing;
  }
  return base;
}

function decodePointer(s: string): string {
  return s.replace(/~1/g, "/").replace(/~0/g, "~");
}

/** Whether a value counts as "empty" for required checks (empty strings and empty arrays are missing). */
export function isBlank(v: unknown): boolean {
  return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
}

export interface ValidateOptions {
  locale?: FormLocale;
  /** Only report issues for these paths (per-step validation). Prefix match, so `parties` covers `parties.0.name`. */
  onlyPaths?: string[];
  /** Skip JsonLogic rules. */
  skipRules?: boolean;
}

/** Strips empty strings so optional string fields the applicant cleared do not trip `minLength`/`format`. Arrays and objects are recursed. */
export function normalise(data: unknown): unknown {
  if (Array.isArray(data)) return data.map(normalise);
  if (data && typeof data === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      const n = normalise(v);
      if (n === "" || n === undefined) continue;
      out[k] = n;
    }
    return out;
  }
  return data;
}

/**
 * Validates a submission against the schema (Ajv), the UI schema (conditional requirement, hidden fields ignored) and cross-field rules.
 * Returns issues in dot-path form ready for the renderer.
 */
export function validateSubmission(schema: FormSchema, ui: UiSchema, rules: FormRule[] | undefined, data: unknown, opts: ValidateOptions = {}): FormIssue[] {
  const locale = opts.locale ?? "en";
  const validate = compileSchema(schema);
  const vis = visibility(ui, data);
  const clean = normalise(data ?? {});
  const issues: FormIssue[] = [];
  const seen = new Set<string>();
  const inScope = (path: string) => (opts.onlyPaths ? opts.onlyPaths.some((p) => path === p || path.startsWith(p + ".")) : true);
  const isVisible = (path: string) => {
    // a field is validated if it or any ancestor is placed and visible
    const segs = path.split(".");
    for (let i = segs.length; i > 0; i--) if (vis.fields.has(segs.slice(0, i).join("."))) return true;
    return vis.fields.size === 0; // no UI placements: validate everything
  };

  validate(clean);
  for (const err of validate.errors ?? []) {
    const path = toDotPath(err);
    if (!inScope(path) || !isVisible(path)) continue;
    const key = `${path}|${err.keyword}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const node = schemaAtPath(schema, path);
    const label = labelOf(node, path, locale);
    const msg = MESSAGES[err.keyword]?.[locale](err.params as Record<string, unknown>, label) ?? `${label}: ${err.message ?? err.keyword}`;
    issues.push({ path, message: msg, severity: "error", keyword: err.keyword });
  }

  // conditional requirement declared in the UI schema
  for (const path of vis.required) {
    if (!inScope(path) || seen.has(`${path}|required`)) continue;
    if (isBlank(getAtPath(clean, path))) {
      const label = labelOf(schemaAtPath(schema, path), path, locale);
      issues.push({ path, message: MESSAGES.required![locale]({}, label), severity: "error", keyword: "required" });
      seen.add(`${path}|required`);
    }
  }

  // widget-level validation (document checklists, tables…) for visible placements
  for (const step of ui.steps) {
    for (const section of step.sections) {
      for (const f of section.fields) {
        if (f.content || !vis.fields.has(f.path) || !inScope(f.path)) continue;
        const node = schemaAtPath(schema, f.path);
        if (!node) continue;
        const widget = getWidget(resolveWidgetKey(node, f));
        if (!widget?.validate) continue;
        for (const issue of widget.validate(getAtPath(clean, f.path), widgetOptions(node, f), locale, f.path)) {
          const key = `${issue.path}|${issue.keyword ?? "widget"}|${issue.message}`;
          if (seen.has(key)) continue;
          seen.add(key);
          issues.push(issue);
        }
      }
    }
  }

  // review-step declaration (whole-form validation only)
  if (!opts.onlyPaths && ui.steps.some((s) => s.review && evaluate(s.visibleWhen, data)) && (data as Record<string, unknown> | undefined)?.[DECLARATION_PATH] !== true) {
    issues.push({ path: DECLARATION_PATH, message: DECLARATION_MESSAGE[locale], severity: "error", keyword: "declaration" });
  }

  if (!opts.skipRules) {
    for (const rule of rules ?? []) {
      const target = rule.fields?.[0] ?? "";
      if (target && !inScope(target)) continue;
      if (target && !isVisible(target)) continue;
      if (evaluate(rule.when, clean)) {
        issues.push({ path: target, message: localize(rule.message, locale) || rule.key, severity: rule.severity ?? "error", ruleKey: rule.key });
      }
    }
  }
  return issues;
}

/** react-hook-form resolver backed by {@link validateSubmission}. Warnings are not treated as errors. */
export function formResolver<T extends FieldValues>(schema: FormSchema, ui: UiSchema, rules: FormRule[] | undefined, locale: FormLocale): Resolver<T> {
  return async (values) => {
    const issues = validateSubmission(schema, ui, rules, values, { locale }).filter((i) => i.severity === "error");
    if (issues.length === 0) return { values, errors: {} };
    const errors: Record<string, unknown> = {};
    for (const issue of issues) {
      const path = issue.path || "root";
      if (errors[path]) continue;
      errors[path] = { type: issue.keyword ?? issue.ruleKey ?? "rule", message: issue.message };
    }
    return { values: {}, errors: errors as FieldErrors<T> };
  };
}

/** Issues grouped by step, so the wizard can flag steps with problems. */
export function issuesByStep(ui: UiSchema, issues: FormIssue[]): Map<string, FormIssue[]> {
  const map = new Map<string, FormIssue[]>();
  for (const step of ui.steps) {
    const paths = step.sections.flatMap((s) => s.fields.map((f) => f.path));
    const mine = issues.filter((i) => paths.some((p) => i.path === p || i.path.startsWith(p + ".")));
    if (mine.length) map.set(step.key, mine);
  }
  return map;
}

/** Authoring-time consistency check. Mirrors `FormValidator.Lint` on the server so the builder can show the same messages before saving. */
export interface DocumentLint { errors: string[]; warnings: string[]; fieldCount: number }

const KNOWN_OPERATORS = new Set(["var", "missing", "missing_some", "if", "?:", "==", "===", "!=", "!==", "!", "!!", "or", "and", ">", ">=", "<", "<=", "max", "min", "+", "-", "*", "/", "%", "map", "filter", "reduce", "all", "none", "some", "merge", "in", "cat", "substr", "log", "isEmpty", "count", "sum", "matches", "any", "daysBetween", "today", "lower", "startsWith", "contains"]);

function unknownOperators(condition: unknown, out: Set<string> = new Set()): Set<string> {
  if (!condition || typeof condition !== "object") return out;
  if (Array.isArray(condition)) { condition.forEach((c) => unknownOperators(c, out)); return out; }
  for (const [op, args] of Object.entries(condition as Record<string, unknown>)) {
    if (!KNOWN_OPERATORS.has(op)) out.add(op);
    unknownOperators(args, out);
  }
  return out;
}

export function lintDocument(schema: FormSchema, ui: UiSchema, rules: FormRule[] | undefined): DocumentLint {
  const errors: string[] = [];
  const warnings: string[] = [];
  let fieldPaths: string[];
  try {
    compileSchema(schema);
    fieldPaths = flattenFields(schema).map((f) => f.path);
  } catch (e) {
    return { errors: [`Schema is not valid JSON Schema: ${(e as Error).message}`], warnings: [], fieldCount: 0 };
  }
  const exists = (p: string) => fieldPaths.includes(p) || fieldPaths.some((fp) => fp.startsWith(p + "."));
  const placed = new Set<string>();
  const steps = ui.steps ?? [];
  if (steps.length === 0) warnings.push("The form has no steps; nothing will render.");
  const stepKeys = new Set<string>();
  for (const step of steps) {
    const id = step.key ?? "";
    if (!id) errors.push("A step has no key.");
    else if (stepKeys.has(id)) errors.push(`Duplicate step key '${id}'.`);
    stepKeys.add(id);
    for (const section of step.sections ?? []) {
      for (const f of section.fields ?? []) {
        if (!f.path) { errors.push(`Step '${id}' has a field without a path.`); continue; }
        if (placed.has(f.path)) warnings.push(`Field '${f.path}' is placed more than once.`);
        placed.add(f.path);
        if (!exists(f.path)) errors.push(`Field '${f.path}' in step '${id}' does not exist in the schema.`);
        if (f.widget && !getWidget(f.widget)) warnings.push(`Field '${f.path}' uses unknown widget '${f.widget}'.`);
        for (const cond of [f.visibleWhen, f.requiredWhen, f.disabledWhen]) for (const v of referencedVars(cond)) if (!exists(v)) warnings.push(`Condition on '${f.path}' references unknown field '${v}'.`);
      }
    }
  }
  for (const p of fieldPaths.filter((p) => !p.includes("."))) {
    const covered = placed.has(p) || [...placed].some((pl) => p.startsWith(pl + "."));
    if (!covered) warnings.push(`Schema field '${p}' is not placed in any step.`);
  }
  const keys = new Set<string>();
  for (const r of rules ?? []) {
    if (!r.key) errors.push("A rule has no key.");
    else if (keys.has(r.key)) errors.push(`Duplicate rule key '${r.key}'.`);
    keys.add(r.key);
    if (r.when === undefined || r.when === null) errors.push(`Rule '${r.key}' has no condition.`);
    if (!localize(r.message, "en").trim()) warnings.push(`Rule '${r.key}' has no English message.`);
    const unknown = unknownOperators(r.when);
    if (unknown.size) errors.push(`Rule '${r.key}' uses unknown operator(s): ${[...unknown].join(", ")}.`);
    for (const v of referencedVars(r.when)) if (!exists(v)) warnings.push(`Rule '${r.key}' references unknown field '${v}'.`);
  }
  return { errors, warnings, fieldCount: countFields(schema) };
}
